'use client'

import { Stack } from '@mui/material'
import type { UIMessage } from 'ai'
import { useState } from 'react'

import AssistantMarkdown from '~/features/assistant/AssistantMarkdown'

import ActionProposalPart from '../parts/ActionProposalPart'
import ArtifactDownload from '../parts/ArtifactDownload'
import ClarificationPart from '../parts/ClarificationPart'
import DashboardPart from '../parts/DashboardPart'
import ThinkingShimmer from '../parts/ThinkingShimmer'
import WorkedSection from './WorkedSection'
import { type AnswerPart, buildTurnModel } from './turnModel'

type AssistantTurnProps = {
  parts: UIMessage['parts']
  readOnly: boolean
  canAnswer: boolean
  onAnswer: (answer: string) => void
  /** Called when an action this turn proposed reaches its outcome. */
  onActionSettled: () => void
  /** The reply is still streaming. */
  isStreaming: boolean
}

/**
 * One assistant turn. The steps are one tree in order for the whole life of the turn. While it streams they run
 * inline, the last one live, with a shimmering "Thinking" line whenever nothing else shows progress. Once it
 * finishes a "Worked" header folds them, open if the reader watched the turn stream and closed if it was loaded
 * finished; a turn of reasoning alone has no header. The final response, approval cards, clarifications,
 * dashboards and file links follow.
 */
export default function AssistantTurn({
  parts,
  readOnly,
  canAnswer,
  onAnswer,
  onActionSettled,
  isStreaming
}: AssistantTurnProps) {
  const { shape, steps, finalResponse, answerParts } = buildTurnModel(parts)
  const [watched, setWatched] = useState(isStreaming)
  if (isStreaming && !watched) setWatched(true)

  // Once the final response starts, the last step is over.
  const lastStep = steps.at(-1)
  const isWorking = isStreaming && finalResponse.length === 0
  const liveStepKey = isWorking ? lastStep?.key : undefined
  // A live run and a streaming reasoning part show their own progress; otherwise the turn shows one "Thinking" line.
  const lastStepShowsProgress =
    liveStepKey !== undefined && (lastStep?.kind === 'toolRun' || (lastStep?.kind === 'thought' && lastStep.streaming))
  const showThinking = isWorking && !lastStepShowsProgress

  return (
    <Stack spacing={1}>
      {steps.length > 0 && (
        <WorkedSection
          steps={steps}
          streaming={isStreaming}
          liveStepKey={liveStepKey}
          showHeader={!isStreaming && shape === 'worked'}
          defaultExpanded={watched}
        />
      )}
      {showThinking && <ThinkingShimmer />}
      {finalResponse.length > 0 && (
        <AssistantMarkdown>{finalResponse.map((part) => part.text.trim()).join('\n\n')}</AssistantMarkdown>
      )}
      {answerParts.map((answerPart) => (
        <AnswerPartView
          key={answerPart.key}
          answerPart={answerPart}
          readOnly={readOnly}
          canAnswer={canAnswer}
          onAnswer={onAnswer}
          onActionSettled={onActionSettled}
        />
      ))}
    </Stack>
  )
}

type AnswerPartViewProps = Pick<AssistantTurnProps, 'readOnly' | 'canAnswer' | 'onAnswer' | 'onActionSettled'> & {
  answerPart: AnswerPart
}

function AnswerPartView({ answerPart, readOnly, canAnswer, onAnswer, onActionSettled }: AnswerPartViewProps) {
  switch (answerPart.kind) {
    case 'proposal':
      return <ActionProposalPart proposal={answerPart.proposal} readOnly={readOnly} onSettled={onActionSettled} />
    case 'clarification':
      return <ClarificationPart clarification={answerPart.clarification} canAnswer={canAnswer} onAnswer={onAnswer} />
    case 'dashboard':
      return <DashboardPart dashboard={answerPart.dashboard} readOnly={readOnly} />
    case 'artifactLink':
      return <ArtifactDownload actionId={answerPart.artifact.actionId} label={answerPart.artifact.filename} />
  }
}
